import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Map as MapIcon, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PropertyCard from "@/components/PropertyCard";
import CategoryChips from "@/components/CategoryChips";
import PropertyMap from "@/components/PropertyMap";
import AlertSubscribe from "@/components/AlertSubscribe";
import StickySearchButton from "@/components/StickySearchButton";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

export default function Properties() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState("list");

  const get = (k, d = "") => params.get(k) || d;

  const catName = categories.find((c) => c.slug === get("category"))?.name;
  const cityName = get("city");
  useSeo({
    title: `${catName || "Property"}${cityName ? ` in ${cityName}` : " across India"}${
      get("bedrooms") ? ` · ${get("bedrooms")}+ BHK` : ""
    }`,
    description: `Browse ${total || ""} verified ${(catName || "property").toLowerCase()} listings${
      cityName ? ` in ${cityName}` : " across India"
    } on Houses7.com — real photos, direct owner contact and instant alerts when new homes match your search.`,
    path: "/properties",
  });

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const p = {};
    ["q", "city", "category", "listing_type", "min_price", "max_price", "bedrooms", "sort"].forEach((k) => {
      const v = params.get(k);
      if (v) p[k] = v;
    });
    try {
      const { data } = await api.get("/properties", { params: p });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const filterFields = (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="filter-panel">
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">Keyword</Label>
        <Input
          defaultValue={get("q")}
          onBlur={(e) => update("q", e.target.value)}
          placeholder="Locality, project…"
          className="h-11 rounded-lg"
          data-testid="filter-keyword-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">Category</Label>
        <Select value={get("category", "all")} onValueChange={(v) => update("category", v)}>
          <SelectTrigger className="h-11 rounded-lg" data-testid="filter-category-select">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.slug} value={c.slug}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">Bedrooms</Label>
        <Select value={get("bedrooms", "all")} onValueChange={(v) => update("bedrooms", v)}>
          <SelectTrigger className="h-11 rounded-lg" data-testid="filter-bedrooms-select">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            {[1, 2, 3, 4, 5].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}+ BHK
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Min ₹</Label>
          <Input
            defaultValue={get("min_price")}
            onBlur={(e) => update("min_price", e.target.value)}
            className="h-11 rounded-lg"
            data-testid="filter-min-price-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Max ₹</Label>
          <Input
            defaultValue={get("max_price")}
            onBlur={(e) => update("max_price", e.target.value)}
            className="h-11 rounded-lg"
            data-testid="filter-max-price-input"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="sticky top-[64px] sm:top-[76px] z-20 bg-background/95 backdrop-blur-md border-b border-border">
        <CategoryChips categories={categories} active={get("category") || null} onSelect={(v) => update("category", v)} />
      </div>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl">
            {categories.find((c) => c.slug === get("category"))?.name || "All properties"}
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground" data-testid="results-count">
            {total} places to explore
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            className="h-11 rounded-lg"
            onClick={() => setView(view === "list" ? "map" : "list")}
            data-testid="map-view-toggle"
          >
            {view === "list" ? <MapIcon className="h-4 w-4 mr-2" /> : <List className="h-4 w-4 mr-2" />}
            {view === "list" ? "Map" : "List"}
          </Button>
          <Button
            variant="outline"
            className="h-11 rounded-lg"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="mobile-filter-toggle"
          >
            {showFilters ? <X className="h-4 w-4 mr-2" /> : <SlidersHorizontal className="h-4 w-4 mr-2" />} Filters
          </Button>
          <Select value={get("sort", "recent")} onValueChange={(v) => update("sort", v)}>
            <SelectTrigger className="h-11 w-[175px] rounded-lg" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Newest first</SelectItem>
              <SelectItem value="price_low">Price: low to high</SelectItem>
              <SelectItem value="price_high">Price: high to low</SelectItem>
              <SelectItem value="popular">Most viewed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {showFilters && (
        <div className="mt-6 rounded-2xl border border-border p-6 fade-up" id="properties-filters">
          {filterFields}
          <Button
            variant="ghost"
            className="mt-5 rounded-lg underline"
            onClick={() => setParams(new URLSearchParams())}
            data-testid="filter-clear-btn"
          >
            Clear all filters
          </Button>
        </div>
      )}

      <div className="mt-9 pb-6">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading listings…</p>
        ) : view === "map" ? (
          <PropertyMap items={items} />
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-border p-12 text-center" data-testid="no-results">
            <p className="font-display text-xl">No matching properties</p>
            <p className="mt-2 text-sm text-muted-foreground">Try widening your budget or clearing filters.</p>
            <Button className="mt-6 rounded-lg" onClick={() => setParams(new URLSearchParams())}>
              Remove all filters
            </Button>
          </div>
        ) : (
          <div
            className="grid gap-x-3.5 gap-y-6 sm:gap-x-6 sm:gap-y-9 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="properties-grid"
          >
            {items.map((p) => (
              <PropertyCard key={p.property_id} prop={p} />
            ))}
          </div>
        )}
      </div>

      <div className="pb-8">
        <AlertSubscribe
          filters={{
            city: get("city") || null,
            category: get("category") || null,
            bedrooms: get("bedrooms") || null,
            max_price: get("max_price") || null,
          }}
        />
      </div>

      <StickySearchButton
        onClick={() => {
          setShowFilters(true);
          setTimeout(() => {
            document.getElementById("properties-filters")?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 60);
        }}
      />
    </div>
  );
}
